/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.inputVersion = 0;
      this.results.id = `${this.input.id}-predictive-search-results`;
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-owns', this.results.id);
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      const handleInput = debounce(this.handleInput.bind(this));
      this.input.addEventListener('input', () => {
        this.clearResults();
        this.inputPending = true;
        handleInput(this.inputVersion);
      });
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.addEventListener('keyup', this.handleKeyup.bind(this));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || (this.hasAttribute('open')
          && this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)'))
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput(version) {
      if (version !== this.inputVersion || !this.isConnected) return;
      this.inputPending = false;
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      if (this.closing) return;
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      if (evt.defaultPrevented || evt.isComposing) return;
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab') && evt.key !== 'Escape') return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          if (this.hasAttribute('open')) {
            evt.preventDefault();
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          if (this.hasAttribute('open')) this.selectOption();
          break;

        case 'Escape':
          if (this.hasAttribute('open') || this.requestController || this.inputPending) {
            evt.preventDefault();
            evt.stopPropagation();
            this.close();
          }
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.clearResults();
      if (!this.isConnected || !searchTerm.length
        || this.unavailableRoute === theme.routes.predictiveSearch) return;

      const controller = new AbortController();
      this.requestController = controller;
      this.setLiveRegionLoadingState();

      try {
        let searchFields = 'title,product_type,variants.title,vendor';
        if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
        if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

        let searchParams = '';
        if (this.productTypeInput && this.productTypeInput.value !== '') {
          searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
        } else {
          searchParams = `q=${encodeURIComponent(searchTerm)}`;
        }

        searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
        searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
          theme.settings.pSearchLimitScope
        }`;
        searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
        searchParams += '&section_id=predictive-search';

        const queryKey = searchParams.replace(' ', '-').toLowerCase();
        if (this.cachedResults[queryKey]) {
          this.renderResults(this.cachedResults[queryKey]);
          return;
        }

        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, {
          signal: controller.signal
        });
        if (this.requestController !== controller) return;
        if (!response.ok) {
          if (response.status === 417) this.unavailableRoute = theme.routes.predictiveSearch;
          throw new Error(response.status);
        }

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        if (this.requestController !== controller || this.getQuery() !== searchTerm) return;

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('Missing predictive search section');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch {
        if (this.requestController === controller) this.clearResults();
      } finally {
        if (this.requestController === controller) {
          this.requestController = null;
          this.clearLoadingState();
        }
      }
    }

    clearResults() {
      this.close();
      this.removeAttribute('results');
      this.results.replaceChildren();
      this.input.setAttribute('aria-autocomplete', 'none');
    }

    clearLoadingState() {
      clearTimeout(this.statusTimeout);
      this.input.removeAttribute('aria-busy');
      this.statusEl.textContent = '';
      this.statusEl.setAttribute('aria-hidden', 'true');
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.input.setAttribute('aria-busy', 'true');
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      clearTimeout(this.statusTimeout);
      this.statusTimeout = setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.results.querySelectorAll('.predictive-search__item').forEach((option, index) => {
        option.id = `${this.input.id}-predictive-option-${index}`;
      });
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.inputVersion += 1;
      this.inputPending = false;
      this.requestController?.abort();
      this.requestController = null;
      this.clearLoadingState();

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
      }

      // Deselect the selected result (if there is one).
      this.querySelectorAll('.predictive-search__item[aria-selected="true"]').forEach((selected) => {
        selected.setAttribute('aria-selected', 'false');
      });

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);

      if (this.results.contains(document.activeElement) || (evt && evt.target === this.resetBtn)) {
        this.closing = true;
        this.input.focus({ preventScroll: true });
        this.closing = false;
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
